import { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo } from 'react';
import { handleMutationResult } from '../../helpers/toastHelper';
import { FIXED_LEAVE_TYPES, FixedLeaveTypeId } from '../availability/availability.types';
import {
    ReadLeaveType,
    Tier,
    useCreateLeaveTypesMutation,
    useDeleteLeaveTypesMutation,
    useGetLeaveTypesQuery,
    useGetSuperAdminOrganisationSettingsQuery,
    useUpdateLeaveTypesMutation
} from '../gql/graphql.generated';
import { LeaveType } from './leave.types';
import { convertFromGqlLeaveTypes, convertLeaveTypeToGql, patchUpTapNumbers } from './leaveManagementHelper';

export interface LeaveTypesStore {
  leaveTypes: LeaveType[];
  refetch: () => void;
}

export const LeaveTypesContext = createContext<LeaveTypesStore>({ leaveTypes: [], refetch: () => {} });

export const LeaveTypesProvider: FC<PropsWithChildren> = ({ children }) => {
  const [{ data: organisationSettingsData }] = useGetSuperAdminOrganisationSettingsQuery();
  const [{ data }, refetch] = useGetLeaveTypesQuery();
  const advanced = organisationSettingsData?.superAdminOrganisationSettings?.leaveTypeTier === Tier.Advanced;

  const store = useMemo(() => {
    const leaveTypesData = data?.leaveTypes || [];

    const rosterDayOff = leaveTypesData.find(lt => lt.id === FixedLeaveTypeId.ROSTER);
    const paidLeave = leaveTypesData.find(lt => lt.id === FixedLeaveTypeId.PAID);

    if (!rosterDayOff || !paidLeave) {
      throw Error('Unable to get Paid and RDO leave types, which must exist in all SnpsShift tenant databases');
    }

    const basicLeaveTypes: ReadLeaveType[] = [rosterDayOff, paidLeave];

    let leaveTypes: ReadLeaveType[];
    if (advanced) {
      const advancedTypes = leaveTypesData.filter(l => !FIXED_LEAVE_TYPES.includes(l.id));
      leaveTypes = [...basicLeaveTypes, ...advancedTypes];
    } else {
      leaveTypes = basicLeaveTypes;
    }

    return { leaveTypes: convertFromGqlLeaveTypes(leaveTypes), refetch };
  }, [data, advanced, refetch]);

  return <LeaveTypesContext.Provider value={store}>{children}</LeaveTypesContext.Provider>;
};

export const useLeaveTypes = () => {
  const context = useContext(LeaveTypesContext);

  if (context.leaveTypes.length === 0) {
    throw Error('useLeaveManagement must be used within a LeaveTypesProvider');
  }

  return context.leaveTypes;
};

export const useRefreshLeaveTypes = () => {
  const context = useContext(LeaveTypesContext);

  return context.refetch;
};

export const useEssLeaveTypes = () => {
  const leaveTypes = useLeaveTypes();

  return useMemo(() => {
    const filteredTypes = leaveTypes.filter(lt => {
      let valid = lt.active;

      if (!(FIXED_LEAVE_TYPES.includes(lt.id) || lt.limitSettings.availableInEss)) {
        valid = false;
      }

      return valid;
    });
    const sorted = filteredTypes.sort((a, b) => {
      const aTap = a.properties.tapNumber;
      const bTap = b.properties.tapNumber;

      if (aTap === -1) {
        return -1;
      }
      if (bTap === -1) {
        return 1;
      } else {
        return aTap - bTap;
      }
    });

    return patchUpTapNumbers(sorted);
  }, [leaveTypes]);
};

export const useLeaveType = (leaveTypeId?: string) => {
  const leaveTypes = useLeaveTypes();

  const leaveType = leaveTypes.find(lt => lt.id === leaveTypeId);

  if (leaveTypeId && !leaveType) {
    throw Error(`Could not find leave type ${leaveTypeId}`);
  }

  return leaveType;
};

export const useCreateLeaveTypes = () => {
  const [{ fetching }, create] = useCreateLeaveTypesMutation();
  const createLeaveTypes = useCallback(async (leaveTypes: LeaveType[]) => {
    const result = await create({ leaveTypes: leaveTypes.map(lt => convertLeaveTypeToGql(lt, true)) });
    await handleMutationResult(result, { successMessage: 'Leave types saved successfully.', errorMessage: 'Failed to save leave types.' });

    return result;
  }, [create]);

  return { fetching, createLeaveTypes };
};

export const useUpdateLeaveTypes = () => {
  const [{ fetching }, update] = useUpdateLeaveTypesMutation();
  const updateLeaveTypes = useCallback(async (leaveTypes: LeaveType[]) => {
    const result = await update({ leaveTypes: leaveTypes.map(lt => convertLeaveTypeToGql(lt)) });
    await handleMutationResult(result, { successMessage: 'Leave types saved successfully.', errorMessage: 'Failed to save leave types.' });

    return result;
  }, [update]);

  return { fetching, updateLeaveTypes };
};

export const useDeleteLeaveTypes = () => {
  const [{ fetching }, mutation] = useDeleteLeaveTypesMutation();

  const deleteLeaveTypes = useCallback(
    async (ids: string[]) => {
      const idsToDelete = ids.filter(id => !FIXED_LEAVE_TYPES.includes(id));

      const result = await mutation({ ids: idsToDelete });
      await handleMutationResult(result, { successMessage: 'Leave types deleted successfully.', errorMessage: 'Failed to delete leave types.' });

      return result;
    },
    [mutation]
  );

  return { deleteLeaveTypes, fetching };
};
